/**
 * Local Authentication Service
 * Uses the local backend for authentication instead of AWS Cognito
 */

import axios from 'axios';
import { getApiConfig } from '../config/api-config';

// Get environment-aware API URL
const API_URL = getApiConfig().baseURL;

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  role?: string;
  organization_id?: string;
  token?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

class LocalAuthService {
  private currentUser: AuthUser | null = null;
  private token: string | null = null;

  constructor() {
    // Load token from localStorage
    this.token = localStorage.getItem('auth_token');
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<{ isSignInComplete: boolean; user?: AuthUser }> {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      if (response.data.success) {
        const { user, token } = response.data.data;

        this.currentUser = user;
        this.token = token;

        // Store token
        localStorage.setItem('auth_token', token);

        return {
          isSignInComplete: true,
          user: user
        };
      }

      throw new Error(response.data.message || 'Authentication failed');
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw new Error(error.response?.data?.message || 'Authentication failed');
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    this.currentUser = null;
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  /**
   * Get the current authenticated user
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    if (!this.token) {
      return null;
    }

    if (this.currentUser) {
      return this.currentUser;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (response.data.success) {
        this.currentUser = response.data.data.user;
        return this.currentUser;
      }

      return null;
    } catch (error) {
      console.error('Failed to get current user:', error);
      // Clear invalid token
      this.token = null;
      localStorage.removeItem('auth_token');
      return null;
    }
  }

  /**
   * Get the current session
   */
  async currentSession(): Promise<any> {
    if (!this.token) {
      return null;
    }

    return {
      getIdToken: () => ({
        getJwtToken: () => this.token
      })
    };
  }

  /**
   * Reset password
   */
  async forgotPassword(email: string): Promise<void> {
    // Not implemented for local auth
    throw new Error('Password reset not available in local mode');
  }

  /**
   * Confirm password reset
   */
  async confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
    // Not implemented for local auth
    throw new Error('Password reset not available in local mode');
  }

  /**
   * Register a new user
   */
  async signUp(email: string, password: string, attributes?: any): Promise<any> {
    // Not implemented for local auth
    throw new Error('Sign up not available in local mode. Please contact an administrator.');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.token;
  }

  /**
   * Get the current token
   */
  getToken(): string | null {
    return this.token;
  }
}

// Export singleton instance
const localAuthService = new LocalAuthService();
export default localAuthService;